<template>
  <v-card :flat="hideTitle">
    <v-card-title class="primary white--text" v-if="!hideTitle">
      <span>{{$t('updateUserInfo')}}</span>
      <v-spacer/>
      <Icon
      icon="mdi-close"
      iconColor="tertiary"
      :small="false"
      :large="true"
      dataTestId="user-info-close-button"
      :tooltipText="$t('close')"
      @icon-clicked="closeDialog"/>
    </v-card-title>
    <v-card-text class="mt-6">
      <FormWrapper
        ref="form"
        formRef="person-form"
        :hideButton="!showSaveChanges"
        @submit="saveUserInfo"
      >
        <v-row class="mb-1" align="center">
          <v-col cols="12" md="6" lg="3">
            <v-text-field
            v-model="person.firstName"
            color="black"
            maxlength="64"
            counter
            :label="$t('firstName')"
            :rules="[rules.required]"/>
          </v-col>
          <v-col cols="12" md="6" lg="3">
            <v-text-field
            v-model="person.lastName"
            color="black"
            maxlength="64"
            counter
            :label="$t('lastName')"
            :rules="[rules.required]"/>
          </v-col>
          <v-col cols="12" md="6" lg="3">
            <v-text-field
            v-model="person.email"
            color="black"
            maxlength="64"
            :label="$t('email')"
            counter
            :rules="[rules.required, rules.validEmail]">
              <template #append>
                <Icon
                v-if="invalidEmail"
                icon="mdi-alert"
                iconColor="error"
                :small="false"
                :tooltipText="suppressionTooltip"
                @icon-clicked="removeSuppressionHold"/>
              </template>
            </v-text-field>
          </v-col>
          <v-col cols="12" md="6" lg="3">
            <v-text-field
              v-model="person.phoneNumber"
              v-mask="phoneMask"
              :label="$t('phoneNumber')"
              :rules="[rules.required, rules.phoneNumber]"
              data-testid="phone-number"
              color="black"
            />
          </v-col>
          <v-col cols="auto">
            <v-checkbox
              v-model="limitNotificationsToMyTracts"
              :label="$t('limitNotificationsToMyTracts')"
              color="primary"
              data-testid="limit-notifications-to-my-tracts"/>
          </v-col>
        </v-row>
      </FormWrapper>
    </v-card-text>
  </v-card>
</template>
<script>
import { rules } from '@/utils/FormRules.js'
import { mask } from 'vue-the-mask'
import isEqual from 'lodash/isEqual'
import cloneDeep from 'lodash/cloneDeep'
import { mapActions, mapGetters } from 'vuex'
import { ErrorSource, EmailStatus, EmailSuppressionType, NotificationScope } from '@/utils/Enumerations'
export default {
  name: 'UserInfoFormDialog',

  props: {
    hideTitle: {
      type: Boolean,
      default: false
    }
  },

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue')
  },

  directives: {
    mask
  },

  data: () => ({
    EmailStatus,
    EmailSuppressionType,
    person: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: ''
    },
    rules,
    phoneMask: '(###)-###-####',
    status: undefined,
    notificationScope: NotificationScope.All.value
  }),

  computed: {
    ...mapGetters('user', ['userInfo']),

    showSaveChanges () {
      if (this.userInfo) {
        return !isEqual(this.userInfo.person, this.person) || this.userInfo.notificationScope !== this.notificationScope
      } else {
        return true
      }
    },

    limitNotificationsToMyTracts: {
      get () {
        return this.notificationScope === NotificationScope.MyTracts.value
      },
      set (limit) {
        this.notificationScope = limit ? NotificationScope.MyTracts.value : NotificationScope.All.value
      }
    },

    invalidEmail () {
      return this.person.emailStatus === EmailStatus.Invalid.value && !this.emailModified
    },

    emailModified () {
      return this.userInfo?.person?.email !== this.person.email
    },

    suppressionTooltip () {
      const base = EmailSuppressionType.fromInt(this.person.emailSuppressionType)
      return this.suppressionRequiresEmailChange
        ? base
        : `${base} - ${this.$t('clickToRemoveHold')}`
    },

    suppressionRequiresEmailChange () {
      return this.person.emailSuppressionType === EmailSuppressionType.Invalid.value || this.person.emailSuppressionType === EmailSuppressionType.Block.value
    }
  },

  watch: {
    userInfo: {
      handler (val) {
        if (val) {
          if (val.person) {
            this.person = cloneDeep(val.person)
          }
          this.notificationScope = val.notificationScope
        }
      },
      deep: true
    }
  },

  async created () {
    const { status } = await this.getUserInfo()
    this.status = status
    if (this.userInfo) {
      if (this.userInfo.person !== null) {
        this.person = cloneDeep(this.userInfo.person)
      }
      this.notificationScope = this.userInfo.notificationScope
    }
  },

  methods: {
    ...mapActions('user', ['updateUserInfo', 'getUserInfo']),
    ...mapActions('notifications', ['fetchNotifications']),

    closeDialog () {
      this.$emit('close')
    },
    async saveUserInfo () {
      if (!this.$refs.form.$refs['person-form'].validate()) {
        this.setSnackError({
          code: 'Form Validating',
          source: ErrorSource.WebClient,
          message: 'Invalid or missing form fields.'
        })
        return
      }

      const notificationScopeChanged = this.userInfo.notificationScope !== this.notificationScope
      const userInfo = cloneDeep(this.userInfo)
      userInfo.person = this.person
      userInfo.notificationScope = this.notificationScope
      await this.updateUserInfo(userInfo)
      await this.getUserInfo()
      if (notificationScopeChanged) {
        await this.fetchNotifications()
      }
      this.$emit('user-info-added')
    },

    removeSuppressionHold () {
      if (this.suppressionRequiresEmailChange) {
        this.setSnackError(this.$t('emailAddressRequiresChange'))
        return
      }

      this.person.emailStatus = EmailStatus.Active.value
    }
  }
}
</script>
